/**
 * standardAtmosphere.ts
 *
 * Implements the ICAO / US Standard Atmosphere 1976 layered model to convert
 * a pressure level (hPa) into an approximate global-mean geopotential height
 * (km) and temperature (K).
 *
 * This is REAL, textbook atmospheric physics (public domain reference
 * model), used here as a physically grounded placeholder for per-cell NOAA
 * GFS HGT/TMP fields, which vary with latitude/longitude/time and will
 * replace this global-mean approximation once live data ingestion is added.
 *
 * Reference: NOAA/NASA/USAF, "U.S. Standard Atmosphere, 1976".
 */

const GRAVITY = 9.80665; // m/s^2
const MOLAR_MASS_AIR = 0.0289644; // kg/mol
const GAS_CONSTANT = 8.3144598; // J/(mol*K)

interface AtmosphereLayer {
  /** Base geopotential height of this layer, in meters. */
  baseHeightM: number;
  /** Base temperature at the layer's base, in Kelvin. */
  baseTempK: number;
  /** Base pressure at the layer's base, in Pascals. */
  basePressurePa: number;
  /** Lapse rate within this layer, in K/m (0 for isothermal layers). */
  lapseRateKPerM: number;
}

// The seven standard layers of the 1976 model, up to ~85 km.
const LAYERS: AtmosphereLayer[] = [
  { baseHeightM: 0, baseTempK: 288.15, basePressurePa: 101325, lapseRateKPerM: -0.0065 },
  { baseHeightM: 11000, baseTempK: 216.65, basePressurePa: 22632.1, lapseRateKPerM: 0 },
  { baseHeightM: 20000, baseTempK: 216.65, basePressurePa: 5474.89, lapseRateKPerM: 0.001 },
  { baseHeightM: 32000, baseTempK: 228.65, basePressurePa: 868.019, lapseRateKPerM: 0.0028 },
  { baseHeightM: 47000, baseTempK: 270.65, basePressurePa: 110.906, lapseRateKPerM: 0 },
  { baseHeightM: 51000, baseTempK: 270.65, basePressurePa: 66.9389, lapseRateKPerM: -0.0028 },
  { baseHeightM: 71000, baseTempK: 214.65, basePressurePa: 3.95642, lapseRateKPerM: -0.002 }
];

function layerForPressure(pressurePa: number): AtmosphereLayer {
  // Pressure decreases monotonically with height, so find the deepest layer
  // whose base pressure is still >= the target pressure.
  let selected = LAYERS[0];
  for (const layer of LAYERS) {
    if (pressurePa <= layer.basePressurePa) {
      selected = layer;
    }
  }
  return selected;
}

/** Returns geopotential height in meters for a given pressure in hPa (global-mean approximation). */
export function pressureToHeightM(pressureHpa: number): number {
  const pressurePa = pressureHpa * 100;
  const layer = layerForPressure(pressurePa);
  const { baseHeightM, baseTempK, basePressurePa, lapseRateKPerM } = layer;

  if (lapseRateKPerM === 0) {
    return (
      baseHeightM -
      ((GAS_CONSTANT * baseTempK) / (GRAVITY * MOLAR_MASS_AIR)) * Math.log(pressurePa / basePressurePa)
    );
  }

  const exponent = (-lapseRateKPerM * GAS_CONSTANT) / (GRAVITY * MOLAR_MASS_AIR);
  return baseHeightM + (baseTempK / lapseRateKPerM) * (Math.pow(pressurePa / basePressurePa, exponent) - 1);
}

/** Returns the global-mean standard-atmosphere temperature in Kelvin at a given pressure (hPa). */
export function pressureToTemperatureK(pressureHpa: number): number {
  const pressurePa = pressureHpa * 100;
  const layer = layerForPressure(pressurePa);
  const heightM = pressureToHeightM(pressureHpa);
  return layer.baseTempK + layer.lapseRateKPerM * (heightM - layer.baseHeightM);
}

function layerForHeight(heightM: number): AtmosphereLayer {
  // Height increases monotonically with layer index, so find the deepest layer whose
  // base height is still <= the target height.
  let selected = LAYERS[0];
  for (const layer of LAYERS) {
    if (heightM >= layer.baseHeightM) {
      selected = layer;
    }
  }
  return selected;
}

/**
 * Inverse of {@link pressureToHeightM}: returns the approximate pressure (hPa) for a given
 * geopotential height (meters), using the global-mean 1976 Standard Atmosphere.
 */
export function heightMToPressureHpa(heightM: number): number {
  const layer = layerForHeight(heightM);
  const { baseHeightM, baseTempK, basePressurePa, lapseRateKPerM } = layer;

  let pressurePa: number;
  if (lapseRateKPerM === 0) {
    pressurePa =
      basePressurePa * Math.exp((-GRAVITY * MOLAR_MASS_AIR * (heightM - baseHeightM)) / (GAS_CONSTANT * baseTempK));
  } else {
    const base = baseTempK / (baseTempK + lapseRateKPerM * (heightM - baseHeightM));
    const exponent = (GRAVITY * MOLAR_MASS_AIR) / (GAS_CONSTANT * lapseRateKPerM);
    pressurePa = basePressurePa * Math.pow(base, exponent);
  }
  return pressurePa / 100;
}
